import { h } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import register from "preact-custom-element";
import useProduct from "../../helpers/useProduct/useProduct";
import resolveInitialVariant from "../../helpers/resolveInitialVariant";
import { CaretDown } from "../../helpers/icons";
import ProductUpsellContent from "./product-upsell-content";
import { suppressStickyAtc } from "../sticky-atc";
import {
  ADD_TO_CART_SOURCES,
  sourceFromVariant,
  trackViewItem,
} from "../../helpers/dataLayer";

function ProductUpsell({
  productId,
  initialVariantId,
  idPrefix,
  trackView,
  addToCartSource,
}) {
  const [open, setOpen] = useState(false);
  const [product] = useProduct(productId);
  // Total and variant reported by the form, kept after it unmounts on close so
  // the header keeps showing what was configured rather than reverting to the
  // base price and image.
  const [total, setTotal] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const ref = useRef(null);

  // Open and on screen, the upsell's add to bag button would sit alongside the
  // sticky bar's, which adds the main product instead.
  useEffect(() => {
    const el = ref.current;
    if (!open || !el) return;

    const observer = new IntersectionObserver(([entry]) =>
      suppressStickyAtc(el, entry.isIntersecting),
    );
    observer.observe(el);

    return () => {
      observer.disconnect();
      suppressStickyAtc(el, false);
    };
  }, [open]);

  if (!product) return null;

  // Falls back to the variant the options will select on load so the image
  // doesn't change when the upsell is first opened.
  const initialVariant = resolveInitialVariant({ product, initialVariantId });
  const image =
    selectedVariant?.image || initialVariant?.image || product.featuredImage;
  const price = total || initialVariant?.price;
  const titleId = `${idPrefix}title`;
  const panelId = `${idPrefix}panel`;

  return (
    <div
      className={`product-upsell ${open ? "product-upsell--open" : ""}`}
      ref={ref}
    >
      <div className="product-upsell__header">
        <button
          type="button"
          className="product-upsell__expand"
          aria-expanded={open}
          aria-controls={panelId}
          aria-labelledby={titleId}
          onClick={() => {
            if (!open && trackView !== undefined && initialVariant) {
              trackViewItem(sourceFromVariant(initialVariant));
            }
            setOpen(!open);
          }}
        ></button>
        {image?.url && (
          <img
            className="product-upsell__image"
            src={window.getSizedImageUrl(image.url, "x196")}
            alt={image.altText || product.title}
          />
        )}
        <div className="product-upsell__header-content">
          <div className="product-upsell__title" id={titleId}>
            {product.title}
          </div>
          <div className="product-upsell__details">
            <a
              href={product.helpers.url}
              className="product-upsell__details-cta link link--x-small"
              tabIndex={open ? 0 : -1}
              aria-hidden={!open}
            >
              {window.localeStrings.product.addon_see_details}
            </a>
          </div>
        </div>
        {price && (
          <div className="product-upsell__price">
            {window.formatMoney(price.amount, false, price.currencyCode)}
          </div>
        )}
        <span className="product-upsell__caret">
          <CaretDown />
        </span>
      </div>
      <ProductUpsellContent
        open={open}
        id={panelId}
        productId={productId}
        initialVariantId={initialVariantId}
        idPrefix={idPrefix}
        addToCartProperties={ADD_TO_CART_SOURCES[addToCartSource]}
        onTotalChange={setTotal}
        onVariantChange={setSelectedVariant}
      />
    </div>
  );
}

if (!customElements.get("product-upsell")) {
  register(
    ProductUpsell,
    "product-upsell",
    [
      "product-id",
      "initial-variant-id",
      "id-prefix",
      "track-view",
      "add-to-cart-source",
    ],
    {
      shadow: false,
    },
  );
}
